import { useEffect, type ReactNode } from 'react';
import { applyAppearance } from '@/design/appearance';
import {
  WATCHED_MEDIA_QUERIES,
  readSystemPreferences,
  useAppearanceStore,
} from '@/modules/settings/appearanceStore';

/**
 * Binds the appearance store to the document.
 *
 * Renders nothing and owns no markup. All it does is keep `<html>`'s data
 * attributes in step with the resolved settings, which is the entire bridge
 * between preferences and how the application looks. No component below this
 * point ever asks which theme is active.
 */
export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const resolved = useAppearanceStore((state) => state.resolved);
  const observeSystem = useAppearanceStore((state) => state.observeSystem);

  // Follow the operating system while the app is open, so switching the OS to
  // dark at sunset moves the app with it rather than at the next restart.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const onChange = () => observeSystem(readSystemPreferences());
    const lists = WATCHED_MEDIA_QUERIES.map(([query]) => window.matchMedia(query));
    for (const list of lists) list.addEventListener('change', onChange);

    // The OS may have changed between module load and mount.
    onChange();

    return () => {
      for (const list of lists) list.removeEventListener('change', onChange);
    };
  }, [observeSystem]);

  useEffect(() => {
    applyAppearance(document.documentElement, resolved);
  }, [resolved]);

  return <>{children}</>;
}
